// /admin → Shuttle: the campus shuttle tracker's controls and what it has timed.
//
// Its own file and its own fetch (`/api/admin-data?part=shuttle`) for the reason
// RestaurantsAdmin is: it polls while open — the one number worth watching here
// is how long ago the tracker phone last checked in — and re-fetching the whole
// dashboard every fifteen seconds to move that one number would be absurd.
//
// What is on it, and why in this order:
//  1. MODE, because it decides what every phone shows. "Auto" prefers the
//     tracker and falls back to the crowd; the other two exist for the days one
//     source is known to be wrong (a tracker phone left in a drawer is still a
//     fresh, confidently wrong position until somebody picks Crowd).
//  2. TRACKER STATUS — last ping age, and which source is being served right
//     now, because "is the bus on the map" is the first question anyone asks.
//     Then the map itself (ShuttleMap.jsx), because the second question is
//     "and where": the route, its stops and the served position, following
//     the bus until somebody drags it away.
//  3. The controls that cost something: a new password, Revoke (every tracker
//     and simulator token stops at its next ping), and a simulator token that
//     is shown ONCE and stored nowhere.
//  4. The timings. Legs are in DRIVING ORDER with every leg listed, timed or
//     not — a table sorted by count would hide exactly the legs the tracker
//     keeps missing. The mean is what the phones quote; the median beside it is
//     how you tell a leg that is slow from a leg with one lunch break in it.
//  5. RIDER REPORTS — every boarding report, refusals included, by the stop it was
//     nearest. A refusal and its reason is the answer to "students keep
//     pressing it and nothing shows". No coordinates are stored, so none are
//     shown: the stop and a distance are the whole of what this page needs.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartCard, TrendChart, HourChart } from './Charts.jsx';
import ShuttleMap from './ShuttleMap.jsx';
import { fmtTime } from '../utils/adminLabels.js';
import { downloadCsv } from '../utils/adminExport.js';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const POLL_MS = 15 * 1000;

const MODES = [
  { id: 'auto', label: 'Auto', hint: 'Tracker when it is fresh, the crowd otherwise.' },
  { id: 'tracker', label: 'Tracker only', hint: 'Only the phone on the bus. Nothing shown when it is off.' },
  { id: 'crowd', label: 'Crowdsource only', hint: 'Only students’ boarding reports.' },
  // Last, and styled apart below, because it is the one mode that shows every
  // student something untrue. It is a development tool, not a fallback for a
  // quiet day — a demo bus is worse than no bus, since a bus on the map is a
  // reason to stand outside in February.
  { id: 'simulate', label: 'Simulate', hint: 'Replays one recorded loop, forever. Not a real bus.' },
];

const mmss = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  const s = Math.round(Number(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const REFUSED = {
  accuracy: 'GPS too imprecise',
  'off-route': 'Too far from the route',
  'rate-ip': 'Too many from one network',
  'rate-install': 'Same phone within 2 min',
  install: 'No app id',
  'bad-fix': 'No position',
};
const delayWords = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  if (Math.abs(sec) < 60) return 'On time';
  const m = Math.round(Math.abs(sec) / 60);
  return `${m} min ${sec > 0 ? 'late' : 'early'}`;
};
// What the estimate we were serving promised for that stop, against when the
// student says the bus showed up. Positive: the bus ran later than we said.
const etaErrWords = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return '—';
  if (Math.abs(sec) < 60) return 'Spot on';
  const m = Math.round(Math.abs(sec) / 60);
  return `${m} min ${sec > 0 ? 'later' : 'earlier'} than shown`;
};
const VERDICT_WORDS = { early: 'Early', 'on-time': 'On time', late: 'Late' };
const ageText = (ms) => {
  if (ms == null) return 'never';
  const s = Math.round(ms / 1000);
  if (s < 90) return `${s} s ago`;
  const m = Math.round(s / 60);
  return m < 120 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
};

export default function ShuttleAdmin({ token, onExpired }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pingSec, setPingSec] = useState('');
  const [simToken, setSimToken] = useState(null);

  // The caller passes an inline arrow; through a ref so a re-render of /admin
  // does not restart the poll.
  const expiredRef = useRef(onExpired);
  useEffect(() => { expiredRef.current = onExpired; }, [onExpired]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/admin-data?part=shuttle`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { expiredRef.current?.(); return; }
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'Could not load the shuttle.'); return; }
      setData(j); setErr('');
      // Seeded once, then left alone: re-seeding on every poll would fight
      // somebody typing in the box.
      setPingSec((v) => (v === '' ? String(j.pingSec ?? 20) : v));
      setDlDay((v) => (v === '' && j.trackDays?.length ? j.trackDays[0] : v));
    } catch {
      setErr('Could not reach the server.');
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const act = async (op, extra = {}) => {
    setBusy(op); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API_BASE}/api/shuttle-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ op, ...extra }),
      });
      if (r.status === 401) { expiredRef.current?.(); return null; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(j.error || 'That did not go through.'); return null; }
      await load();
      return j;
    } catch {
      setErr('Could not reach the server.');
      return null;
    } finally {
      setBusy('');
    }
  };

  const [lo, hi] = data?.pingSecRange || [1, 120];
  const intervalChanged = data && String(data.pingSec) !== String(pingSec).trim();
  const saveInterval = async () => {
    const sec = Math.round(Number(pingSec));
    if (!Number.isFinite(sec) || sec < lo || sec > hi) { setErr(`The interval must be a whole number of seconds, ${lo} to ${hi}.`); return; }
    if (await act('interval', { sec })) setMsg(`The tracker will ping every ${sec} s.`);
  };

  /**
   * A download is a FETCH, not a link: the row carries the admin token, and an
   * <a href> to the export route would be an unauthenticated request saved as
   * a 401. The CSV itself is written by the same `downloadCsv` every other
   * tab's download goes through.
   */
  const [dlDay, setDlDay] = useState('');
  const [dl, setDl] = useState('');
  const download = async (kind, dayOverride) => {
    setDl(kind); setErr(''); setMsg('');
    try {
      // The override is for the one button that names its own day ("today's
      // pings"), so it cannot be silently retargeted by the selector above it.
      const day = dayOverride || dlDay || '';
      const q = new URLSearchParams({ kind, ...(day ? { day } : {}) });
      const r = await fetch(`${API_BASE}/api/shuttle-export?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { expiredRef.current?.(); return; }
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'That export did not build.'); return; }
      if (!j.rows.length) { setErr(`No ${kind} to download${day ? ` for ${day}` : ''}.`); return; }
      downloadCsv(`shuttle-${kind}${day ? `-${day}` : ''}.csv`, j.header, j.rows);
      setMsg(`${j.rows.length.toLocaleString('en-CA')} rows downloaded.`);
    } catch {
      setErr('Could not reach the server.');
    } finally {
      setDl('');
    }
  };

  const trips = useMemo(() => data?.trips || [], [data]);
  const reports = useMemo(() => data?.reports || [], [data]);
  // Today's ACCEPTED reports per stop, in route order — the ones that moved
  // the bus on everybody's map.
  const reportsByStop = useMemo(() => {
    if (!data) return [];
    const today = reports.length ? reports[0].day : null;
    const n = new Map();
    for (const x of reports) if (x.accepted && x.stop && x.day === today) n.set(x.stop, (n.get(x.stop) || 0) + 1);
    return (data.stops || []).map((st) => ({ ...st, n: n.get(st.id) || 0 }));
  }, [data, reports]);
  const trend = useMemo(() => {
    const by = new Map();
    for (const t of trips) {
      if (!by.has(t.day)) by.set(t.day, []);
      by.get(t.day).push(t.sec);
    }
    return [...by.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1)) // oldest first — see TrendChart
      .map(([day, xs]) => ({ day, value: Math.round((xs.reduce((s, x) => s + x, 0) / xs.length / 60) * 10) / 10 }));
  }, [trips]);
  const byHour = useMemo(() => {
    const sum = Array(24).fill(0); const n = Array(24).fill(0);
    for (const t of trips) { sum[t.hour] += t.sec; n[t.hour] += 1; }
    return sum.map((s, h) => (n[h] ? Math.round((s / n[h] / 60) * 10) / 10 : 0));
  }, [trips]);

  if (!data) return <div style={S.muted}>{err || 'Loading the shuttle…'}</div>;

  const tr = data.tracker;
  const trackerLive = tr && !tr.sim && tr.ageMs != null && tr.ageMs < 3 * 60 * 1000;

  return (
    <div>
      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      <div style={S.section}>
        <div style={S.label}>Where the public position comes from</div>
        <div style={S.modes}>
          {MODES.map((m) => (
            <button key={m.id}
                    style={{ ...S.mode, ...(m.id === 'simulate' ? S.modeSim : {}), ...(data.mode === m.id ? (m.id === 'simulate' ? S.modeSimOn : S.modeOn) : {}) }}
                    disabled={busy === 'mode'} onClick={() => act('mode', { mode: m.id })}>
              <div style={S.modeName}>{m.label}</div>
              <div style={{ ...S.modeHint, ...(data.mode === m.id ? S.modeHintOn : {}) }}>{m.hint}</div>
            </button>
          ))}
        </div>
      </div>

      {data.mode === 'simulate' && (
        <div style={S.simBanner}>
          <b>Simulating.</b> Every phone is being shown a recorded loop rather than the bus — the tracker and
          rider reports are ignored while this is on, though both keep recording. The recording is{' '}
          {data.replay?.samples || 0} pings{data.replay?.recordedFrom
            ? ` from ${new Date(data.replay.recordedFrom).toLocaleDateString('en-CA')}`
            : ''}, one lap of {data.replay?.loopMs ? `${Math.round(data.replay.loopMs / 60000 * 10) / 10} min` : '—'},
          repeating on the hour and every hour. Put it back to <b>Auto</b> before anybody relies on it.
        </div>
      )}

      <div style={S.stats}>
        <Stat label="Serving now"
              value={data.serving ? (data.serving === 'simulate' ? 'Simulation' : data.serving === 'tracker' ? 'Tracker' : 'Crowd') : 'Nothing'}
              hint={data.serving ? (data.serving === 'simulate' ? 'a recording, not the bus' : '') : 'No fresh position from the chosen source.'}
              warn={!data.serving} />
        <Stat label="Tracker last ping" value={ageText(tr?.ageMs)}
              hint={tr ? `${tr.sim ? 'simulator · ' : ''}accuracy ${tr.accuracy ?? '—'} m` : 'No ping has ever arrived.'}
              warn={!trackerLive} />
        <Stat label="Tracker battery" value={tr?.battery == null ? '—' : `${tr.battery}%`}
              hint={tr?.battery == null
                ? 'The tracker has not reported one.'
                : `${tr.charging ? 'charging' : 'on battery'}${tr.lowPower ? ' · Low Power Mode' : ''}`}
              warn={tr?.battery != null && !tr.charging && tr.battery <= 20} />
        <Stat label="Last refused ping" value={data.lastRefusal ? data.lastRefusal.reason : 'none'}
              hint={data.lastRefusal ? `${fmtTime(data.lastRefusal.rx)}${data.lastRefusal.sim ? ' · simulator' : ''}` : ''} />
        <Stat label="Ping interval" value={data.pingSec ? `${data.pingSec}s` : '—'} hint="tracker sends this often" />
        <Stat label="Password" value={data.passwordSource === 'admin' ? 'Set here' : data.passwordSource === 'env' ? 'From env' : 'Not set'}
              hint={`session epoch ${data.epoch}`} warn={data.passwordSource === 'none'} />
      </div>

      <ShuttleMap position={data.position} serving={data.serving}
                  seenText={data.position ? ageText(Math.max(0, data.serverTime - data.position.t)) : ''} />

      <div style={S.section}>
        <div style={S.label}>Which phone is the bus</div>
        {(data.devices || []).length === 0 ? (
          <div style={S.note}>No tracker has pinged yet. A phone appears here the first time it is heard from.</div>
        ) : (
          <div style={S.devices}>
            <button style={{ ...S.device, ...(!data.source ? S.deviceOn : {}) }}
                    disabled={busy === 'source'} onClick={() => act('source', { device: '' })}>
              <div style={S.deviceName}>Auto</div>
              <div style={S.deviceHint}>whichever pinged most recently</div>
            </button>
            {(data.devices || []).map((d) => {
              const live = d.ageMs != null && d.ageMs < 3 * 60 * 1000;
              const serving = data.source ? data.source === d.id : d.id === data.tracker?.device;
              return (
                <button key={d.id} style={{ ...S.device, ...(data.source === d.id ? S.deviceOn : {}) }}
                        disabled={busy === 'source'} onClick={() => act('source', { device: d.id })}>
                  <div style={S.deviceName}>
                    {d.id}
                    {serving && <span style={S.servingTag}>on the map</span>}
                  </div>
                  <div style={S.deviceHint}>
                    {ageText(d.ageMs)}{live ? '' : ' · quiet'}
                    {d.accuracy != null && ` · ±${d.accuracy} m`}
                    {d.battery != null && ` · ${d.battery}%${d.charging ? ' charging' : ''}`}
                    {d.offRoute && ' · off route'}
                  </div>
                  <span style={S.forget} title={`Forget ${d.id}`} role="button" tabIndex={-1}
                        onClick={(e) => { e.stopPropagation(); if (window.confirm(`Forget ${d.id}? It reappears the next time it pings.`)) act('forget-device', { device: d.id }); }}>
                    ✕
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div style={S.note}>
          Every tracker that pings is listed, whether or not it is the one being served. <b>Auto</b> follows
          whichever spoke last, which alternates when two are live — pin one and the other keeps recording
          without moving the bus. A phone nobody pinned never contributes leg timings, so a second tracker
          cannot blend two buses into one average.
        </div>
      </div>

      <div style={S.section}>
        <div style={S.label}>Testing</div>
        <button style={{ ...S.toggle, ...(data.allowOffRoute ? S.toggleOn : {}) }}
                disabled={busy === 'offroute'}
                onClick={() => act('offroute', { on: !data.allowOffRoute })}>
          <span style={S.toggleDot(data.allowOffRoute)} />
          Accept pings from off the route
        </button>
        {data.allowOffRoute && (
          <div style={S.warn}>
            <b>On.</b> A phone anywhere in the world can put the bus on the map — its position is snapped to
            the nearest point on the loop. Students see whatever it says. Turn this off when you have
            finished testing.
          </div>
        )}
        <div style={S.note}>
          Off, a ping more than {120} m from the road is refused as <code>off-route</code>, which is what
          keeps a stray phone off the map. On, it is accepted and snapped to the loop so you can test from a
          desk. Off-route pings are <b>never</b> allowed to produce leg timings, trips or stop arrivals —
          those are the numbers the ETAs are built from — but they do appear in the day's track download.
        </div>
      </div>

      <div style={S.actions}>
        <div style={S.group}>
          <div style={S.label}>Change the shuttle password</div>
          <div style={S.row}>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password"
                   style={S.input} autoComplete="new-password" />
            <button style={S.btn} disabled={!newPw || busy === 'password'} onClick={async () => {
              const j = await act('password', { password: newPw });
              if (j) { setNewPw(''); setMsg('Password changed. Signed-in trackers keep working until you revoke them.'); }
            }}>Save</button>
          </div>
          <div style={S.note}>At least 8 characters with a capital, a number and a symbol. Replaces SHUTTLE_PASSWORD.</div>
        </div>

        <div style={S.group}>
          <div style={S.label}>Ping interval</div>
          <div style={S.row}>
            <input type="number" min={lo} max={hi} step={1} value={pingSec} style={{ ...S.input, flex: '0 0 90px' }}
                   onChange={(e) => setPingSec(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') saveInterval(); }} />
            <span style={S.unit}>seconds</span>
            <button style={S.btn} disabled={busy === 'interval' || !intervalChanged} onClick={saveInterval}>Save</button>
          </div>
          <div style={S.note}>
            Whole seconds, {lo}–{hi}. How often the phone on the bus sends its position; the tracker picks a
            change up within one interval, so nobody has to touch the phone. A ping closer than half the
            interval is refused. 1–5 s is for MEASURING the route (see the downloads below) — it is a
            battery and a bill left on all day.
          </div>
        </div>

        <div style={S.group}>
          <div style={S.label}>Sessions</div>
          <button style={S.btnDanger} disabled={busy === 'revoke'} onClick={async () => {
            if (!window.confirm('Sign out every tracker phone and simulator? The phone on the bus will need a new emailed code.')) return;
            const j = await act('revoke');
            if (j) setMsg(`Revoked. Session epoch is now ${j.epoch}.`);
          }}>Revoke tracker sessions</button>
          <div style={S.note}>Every tracker and simulator token stops at its next ping.</div>
        </div>

        <div style={S.group}>
          <div style={S.label}>Simulator</div>
          <button style={S.btn} disabled={busy === 'sim-token'} onClick={async () => {
            const j = await act('sim-token');
            if (j) setSimToken(j);
          }}>Generate simulator token</button>
          {simToken && (
            <div style={S.simBox}>
              <div style={S.note}>Shown once, valid 2 hours. Its pings move the public position but are never counted in timings.</div>
              <textarea readOnly value={simToken.token} style={S.tokenArea} onFocus={(e) => e.target.select()} />
              <div style={S.row}>
                <button style={S.btn} onClick={() => navigator.clipboard?.writeText(simToken.token).then(() => setMsg('Token copied.'), () => {})}>Copy</button>
                <button style={S.btnGhost} onClick={() => setSimToken(null)}>Hide</button>
              </div>
              <code style={S.code}>node scripts/shuttle-sim.mjs --base https://www.myumaps.ca --token &lt;token&gt; --speed 10</code>
            </div>
          )}
          {data.simLegs.length > 0 && (
            <div style={S.note}>The simulator has timed {data.simLegs.length} leg{data.simLegs.length === 1 ? '' : 's'} in the last day — kept apart, not in the table below.</div>
          )}
        </div>
      </div>

      <div style={S.section}>
        <div style={S.label}>Legs, in driving order <span style={S.labelHint}>· over the last {data.statsWindow.toLocaleString('en-CA')} timed legs, outliers over 20 min excluded, simulator never</span></div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr><th style={S.th}>From → to</th><th style={S.thNum}>Road m</th><th style={S.thNum}>n</th><th style={S.thNum}>Mean</th><th style={S.thNum}>Median</th><th style={S.thNum} title="The lower quartile — the figure the apps quote, so a bus runs late against it more often than early.">Quoted (p25)</th></tr>
            </thead>
            <tbody>
              {data.legs.map((l) => (
                <tr key={l.key}>
                  <td style={S.td}>{l.fromName} → {l.toName}</td>
                  <td style={S.tdNum}>{l.metres}</td>
                  <td style={{ ...S.tdNum, ...(l.n === 0 ? S.dim : {}) }}>{l.n}</td>
                  <td style={S.tdNum}>{mmss(l.mean)}</td>
                  <td style={S.tdNum}>{mmss(l.median)}</td>
                  <td style={S.tdNum}>{mmss(l.p25)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={S.charts}>
        <ChartCard title="Loop duration" hint="Mean minutes per full loop, by day" empty={trend.length ? null : 'No complete loop timed yet.'}>
          <TrendChart points={trend} label="minutes per loop" />
        </ChartCard>
        <ChartCard title="Loop duration by hour" hint="Mean minutes, by the hour the loop started (Winnipeg)" empty={trips.length ? null : 'No complete loop timed yet.'}>
          <HourChart values={byHour} />
        </ChartCard>
      </div>

      <div style={S.section}>
        <div style={S.label}>
          Download data
          <span style={S.labelHint}> · CSV, for working out the timetable: when the service runs, what each
            leg really takes, and how fast the bus moves where</span>
        </div>
        <div style={S.row}>
          <select style={{ ...S.input, flex: '0 0 170px' }} value={dlDay} onChange={(e) => setDlDay(e.target.value)}>
            <option value="">All days (not for the track)</option>
            {(data.trackDays || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button style={S.btnGhost} disabled={!!dl || !dlDay} onClick={() => download('track')}>
            {dl === 'track' ? 'Building…' : 'Position track'}
          </button>
          <button style={S.btnGhost} disabled={!!dl} onClick={() => download('splits')}>
            {dl === 'splits' ? 'Building…' : 'Stop-to-stop splits'}
          </button>
          <button style={S.btnGhost} disabled={!!dl} onClick={() => download('trips')}>
            {dl === 'trips' ? 'Building…' : 'Loops'}
          </button>
          <button style={S.btnGhost} disabled={!!dl} onClick={() => download('arrivals')}>
            {dl === 'arrivals' ? 'Building…' : 'Arrivals'}
          </button>
          <button style={S.btnGhost} disabled={!!dl} onClick={() => download('reports')}>
            {dl === 'reports' ? 'Building…' : 'Rider reports'}
          </button>
        </div>
        <div style={S.note}>
          <b>Position track</b> is every ping for one day: where the bus was along the loop, its lat/lng,
          which leg it was on, and its speed — measured from two loop positions and two clocks, not from
          the phone's own reading, which plenty of browsers never give. A day is required, since a day of
          one-second pings is already tens of thousands of rows. <b>Splits</b> is each stop-to-stop leg's
          mean and median and the speed those imply, all-time and for the chosen day. <b>Loops</b> is every
          complete circuit and its duration. Kept 30 days.
        </div>
      </div>

      <div style={S.section}>
        <div style={S.label}>
          Rider reports ({reports.length})
          <span style={S.labelHint}> · every "I've gotten on the bus", refusals included, newest first · an accepted one places the bus for 5 min and is run forward on the pace for up to an hour after · <b>held</b>: it disagreed with where the bus was thought to be, and is not served unless another rider or the tracker agrees within 10 min</span>
        </div>
        {reports.length === 0 ? <div style={S.muted}>Nobody has reported the shuttle yet.</div> : (
          <>
            <div style={S.stopCounts}>
              {reportsByStop.map((st) => (
                <div key={st.id} style={{ ...S.stopCount, ...(st.n ? {} : S.dim) }}>
                  <span style={S.stopCountN}>{st.n}</span> {st.name}
                </div>
              ))}
            </div>
            <div style={S.note}>Accepted today, by nearest stop.</div>
            <div style={{ ...S.tableWrap, maxHeight: 420, overflowY: 'auto', marginTop: 8 }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Time</th><th style={S.th}>Nearest stop</th><th style={S.thNum}>m</th>
                    <th style={S.th}>Timetable</th><th style={S.th}>Our estimate</th>
                    <th style={S.th}>Rider says</th><th style={S.th}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((x, i) => (
                    <tr key={`${x.t}-${i}`}>
                      <td style={S.td}>{fmtTime(x.t)}</td>
                      <td style={S.td}>{x.stopName || <span style={S.dim}>{x.far ? 'Off campus' : '—'}</span>}</td>
                      <td style={S.tdNum}>{x.metres ?? '—'}</td>
                      <td style={S.td}>{x.accepted ? delayWords(x.delaySec) : <span style={S.dim}>—</span>}</td>
                      <td style={S.td}>
                        {x.accepted ? etaErrWords(x.etaErrSec) : <span style={S.dim}>—</span>}
                        {x.accepted && x.etaBasis === 'sched' && <span style={S.dim}> · timetable</span>}
                      </td>
                      <td style={S.td}>{VERDICT_WORDS[x.verdict] || <span style={S.dim}>—</span>}</td>
                      <td style={S.td}>
                        {x.accepted
                          ? <span style={S.okText}>Accepted</span>
                          : <span style={S.badText}>{REFUSED[x.reason] || x.reason}</span>}
                        {x.accepted && x.held && (
                          <span
                            style={S.heldTag}
                            title="Disagreed with where the bus was thought to be — not served unless another rider or the tracker agreed within 10 min"
                          >
                            held
                          </span>
                        )}
                        {x.accuracy != null && <span style={S.dim}> · ±{x.accuracy} m</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={S.section}>
        <div style={S.label}>Today's arrivals ({data.arrivals.length})</div>
        {/* The arrivals below are one row per stop the bus reached — about one
            a minute. This line is the raw evidence underneath them, which at a
            5 s interval moves twelve times between two arrivals: it is how you
            tell "recording" from "the phone has gone to sleep" without waiting
            for the next stop. */}
        <div style={S.trackLive}>
          <span>
            <b>{(data.track?.n || 0).toLocaleString('en-CA')}</b> ping{data.track?.n === 1 ? '' : 's'} recorded today
            {data.track?.newestT ? ` · newest ${ageText(data.serverTime - data.track.newestT)}` : ''}
          </span>
          <button
            style={S.btnGhost}
            disabled={!!dl || !data.track?.n}
            onClick={() => download('track', data.track?.day)}
          >
            {dl === 'track' ? 'Building…' : "Download today's pings"}
          </button>
        </div>
        {data.arrivals.length === 0 ? <div style={S.muted}>None yet today.</div> : (
          <div style={{ ...S.tableWrap, maxHeight: 360, overflowY: 'auto' }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Time</th><th style={S.th}>Stop</th></tr></thead>
              <tbody>
                {data.arrivals.map((a, i) => (
                  <tr key={`${a.t}-${i}`}><td style={S.td}>{fmtTime(a.t)}</td><td style={S.td}>{a.name}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={S.note}>The tracker phone signs in at <a href="/shuttle/track">/shuttle/track</a>.</div>
    </div>
  );
}

function Stat({ label, value, hint, warn }) {
  return (
    <div style={{ ...S.stat, ...(warn ? S.statWarn : {}) }}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  );
}

const S = {
  muted: { color: '#5f6368', fontSize: 14, margin: '6px 0 12px' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  devices: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  device: {
    position: 'relative', textAlign: 'left', cursor: 'pointer', background: '#fff',
    border: '1px solid #dadce0', borderRadius: 10, padding: '10px 30px 10px 12px', minWidth: 190,
    font: 'inherit', color: '#202124',
  },
  deviceOn: { borderColor: '#1a73e8', background: '#e8f0fe', boxShadow: 'inset 0 0 0 1px #1a73e8' },
  deviceName: { fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  deviceHint: { fontSize: 12, color: '#5f6368', marginTop: 2 },
  servingTag: {
    fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
    background: '#e6f4ea', color: '#188038', borderRadius: 999, padding: '2px 6px',
  },
  forget: { position: 'absolute', top: 6, right: 8, color: '#80868b', fontSize: 13, cursor: 'pointer' },
  toggle: {
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', font: 'inherit',
    background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '8px 14px',
    color: '#202124', marginTop: 8,
  },
  toggleOn: { borderColor: '#b06000', background: '#fef7e0', color: '#b06000', fontWeight: 700 },
  toggleDot: (on) => ({
    width: 10, height: 10, borderRadius: 999, background: on ? '#b06000' : '#dadce0', display: 'inline-block',
  }),
  warn: {
    marginTop: 8, background: '#fef7e0', border: '1px solid #f9e3a2', color: '#7a4b00',
    borderRadius: 8, padding: '8px 10px', fontSize: 13, lineHeight: 1.45,
  },
  section: { marginBottom: 16 },
  label: { fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  labelHint: { textTransform: 'none', letterSpacing: 0, fontWeight: 600 },
  modes: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 },
  mode: { textAlign: 'left', background: '#fff', border: '1.5px solid #dadce0', borderRadius: 12, padding: '10px 12px', cursor: 'pointer', color: '#202124' },
  modeOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  modeName: { fontSize: 15, fontWeight: 800 },
  modeHint: { fontSize: 12, color: '#5f6368', marginTop: 2 },
  modeHintOn: { color: '#e8f0fe' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 },
  stat: { background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1px solid #dadce0' },
  statWarn: { borderColor: '#f3b8b4', background: '#fef7f6' },
  statValue: { fontSize: 20, fontWeight: 800 },
  statLabel: { fontSize: 12, fontWeight: 700, color: '#5f6368', marginTop: 2 },
  statHint: { fontSize: 12, color: '#80868b', marginTop: 4 },
  actions: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 16 },
  group: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '9px 12px 11px', flex: '1 1 260px', minWidth: 0 },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  input: { flex: '1 1 160px', fontSize: 14, padding: '8px 10px', border: '1.5px solid #dadce0', borderRadius: 8, outline: 'none' },
  btn: { background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnOn: { background: '#1a73e8', color: '#fff', borderColor: '#1a73e8' },
  unit: { fontSize: 13, color: '#5f6368', fontWeight: 700 },
  btnDanger: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3b8b4', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  note: { fontSize: 12, color: '#5f6368', lineHeight: 1.45, marginTop: 8 },
  modeSim: { borderStyle: 'dashed' },
  modeSimOn: { background: '#8430ce', borderColor: '#8430ce', color: '#fff', borderStyle: 'solid' },
  simBanner: {
    background: '#f3e8fd', border: '1px solid #d6b4f5', borderRadius: 10,
    padding: '10px 12px', fontSize: 13, lineHeight: 1.5, color: '#3c1361', marginBottom: 16,
  },
  trackLive: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    background: '#f1f3f4', border: '1px solid #e0e3e7', borderRadius: 8,
    padding: '8px 12px', fontSize: 13, color: '#202124', margin: '0 0 10px',
  },
  simBox: { marginTop: 8 },
  tokenArea: { width: '100%', boxSizing: 'border-box', minHeight: 70, fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 11, padding: 8, border: '1.5px solid #dadce0', borderRadius: 8, margin: '8px 0', wordBreak: 'break-all' },
  code: { display: 'block', fontSize: 11, background: '#f1f3f4', borderRadius: 6, padding: '6px 8px', marginTop: 8, wordBreak: 'break-all' },
  tableWrap: { background: '#fff', borderRadius: 12, border: '1px solid #dadce0', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #dadce0', fontSize: 12, color: '#5f6368' },
  thNum: { textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid #dadce0', fontSize: 12, color: '#5f6368' },
  td: { padding: '7px 12px', borderBottom: '1px solid #f1f3f4' },
  tdNum: { padding: '7px 12px', borderBottom: '1px solid #f1f3f4', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  dim: { color: '#9aa0a6' },
  okText: { color: '#137333', fontWeight: 700 },
  badText: { color: '#c5221f', fontWeight: 700 },
  // A held report is accepted, not refused, so it is neither green nor red.
  heldTag: {
    marginLeft: 6, padding: '0 6px', borderRadius: 8, fontSize: 11, fontWeight: 700,
    color: '#8a4b00', background: '#fef3e0', border: '1px solid #f5d9a8',
  },
  stopCounts: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  stopCount: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '4px 10px', fontSize: 13 },
  stopCountN: { fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  charts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, marginBottom: 16 },
};
